import { useState, useCallback, useEffect, useRef, RefObject, Dispatch, SetStateAction } from 'react';
import { Employee, PostgresConfig } from '../types';
import { getPostgresApiBaseUrl, getPostgresClient } from '../services/postgresClient';
import { loadEmployees } from '../services/rhDataService';
import { sortItemsByEmployeeName } from '../utils/employeeSort';
import { EMPLOYEES_SYNC_INTERVAL_MS } from './appDefaults';
import { computeSyncBackoffMs } from '../utils/syncBackoff';

/** Intervalo de polling do /health quando API está online. */
const HEALTH_POLL_INTERVAL_MS = 60_000;

export type ApiConnectionState = 'checking' | 'online' | 'offline';

export interface UseBoardSyncParams {
  isAuthenticated: boolean;
  currentCompanyId: string | null;
  isDataLoadedRef: RefObject<boolean>;
  postgresConfig: PostgresConfig;
  setItems: Dispatch<SetStateAction<Record<string, Employee[]>>>;
  setColumnKeyMap: Dispatch<SetStateAction<Record<string, string>>>;
}

/**
 * Conexão com a API, health polling, reconexão e sync de colaboradores com backoff.
 * @param params - IDs de empresa e setters do estado de colaboradores
 * @returns Config Postgres, status de conexão e refresh manual
 */
export function useBoardSync({
  isAuthenticated,
  currentCompanyId,
  isDataLoadedRef,
  postgresConfig,
  setItems,
  setColumnKeyMap,
}: UseBoardSyncParams) {
  const [isDbConnected, setIsDbConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ApiConnectionState>('checking');
  const boardEtagRef = useRef<string | null>(null);
  const wasOfflineRef = useRef(false);
  const refreshEmployeesRef = useRef<
    (options?: { force?: boolean }) => Promise<boolean>
  >(async () => true);

  const getSB = useCallback(() => getPostgresClient(postgresConfig.url), [postgresConfig.url]);

  useEffect(() => {
    boardEtagRef.current = null;
  }, [currentCompanyId]);

  const refreshEmployeesFromDb = useCallback(
    async (options?: { force?: boolean }): Promise<boolean> => {
      if (!currentCompanyId || !isAuthenticated || !isDataLoadedRef.current) return true;
      const sb = getSB();
      try {
        if (!options?.force) {
          const version = await sb.fetchEmployeesBoardVersion(
            currentCompanyId,
            boardEtagRef.current,
          );
          if (!version.ok) throw version.error;

          if (version.unchanged) {
            boardEtagRef.current = version.etag;
            return true;
          }

          if (boardEtagRef.current === null) {
            boardEtagRef.current = version.etag;
            return true;
          }

          boardEtagRef.current = version.etag;
        }

        const { items: fresh, columnKeyMap: newMap } = await loadEmployees(sb, currentCompanyId);
        setItems(sortItemsByEmployeeName(fresh));
        setColumnKeyMap(newMap);

        const versionAfter = await sb.fetchEmployeesBoardVersion(currentCompanyId);
        if (versionAfter.ok) boardEtagRef.current = versionAfter.etag;

        return true;
      } catch (e) {
        console.warn('[sync] Falha ao atualizar lista de funcionários', e);
        return false;
      }
    },
    [currentCompanyId, isAuthenticated, getSB, isDataLoadedRef, setItems, setColumnKeyMap],
  );

  useEffect(() => {
    refreshEmployeesRef.current = refreshEmployeesFromDb;
  }, [refreshEmployeesFromDb]);

  /** Polling de /health — 60s online, backoff quando offline; refresh ao reconectar. */
  useEffect(() => {
    let cancelled = false;
    let timeoutId = 0;
    let failCount = 0;

    const scheduleNext = (delayMs: number) => {
      timeoutId = window.setTimeout(() => {
        void pollHealth();
      }, delayMs);
    };

    const pollHealth = async () => {
      const base = getPostgresApiBaseUrl(postgresConfig.url);
      const healthUrl = base ? `${base}/health` : '/health';

      try {
        const res = await fetch(healthUrl);
        if (cancelled) return;

        if (!res.ok) throw new Error(`health ${res.status}`);

        if (wasOfflineRef.current && isAuthenticated && currentCompanyId && isDataLoadedRef.current) {
          void refreshEmployeesRef.current({ force: true });
        }

        wasOfflineRef.current = false;
        failCount = 0;
        setIsDbConnected(true);
        setConnectionState('online');
        scheduleNext(HEALTH_POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        wasOfflineRef.current = true;
        failCount += 1;
        setIsDbConnected(false);
        setConnectionState('offline');
        scheduleNext(computeSyncBackoffMs(failCount));
      }
    };

    void pollHealth();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [postgresConfig.url, isAuthenticated, currentCompanyId, isDataLoadedRef]);

  useEffect(() => {
    if (!isAuthenticated || !currentCompanyId) return;

    let cancelled = false;
    let failCount = 0;
    let timeoutId = 0;

    const scheduleNext = (delayMs: number) => {
      timeoutId = window.setTimeout(async () => {
        if (cancelled) return;
        if (!isDataLoadedRef.current) {
          scheduleNext(EMPLOYEES_SYNC_INTERVAL_MS);
          return;
        }
        const ok = await refreshEmployeesFromDb();
        if (ok) {
          failCount = 0;
          scheduleNext(EMPLOYEES_SYNC_INTERVAL_MS);
        } else {
          failCount += 1;
          scheduleNext(computeSyncBackoffMs(failCount));
        }
      }, delayMs);
    };

    scheduleNext(EMPLOYEES_SYNC_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timeoutId);
      failCount = 0;
      void refreshEmployeesFromDb().finally(() => {
        if (!cancelled) scheduleNext(EMPLOYEES_SYNC_INTERVAL_MS);
      });
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, currentCompanyId, refreshEmployeesFromDb, isDataLoadedRef]);

  return {
    isDbConnected,
    connectionState,
    setIsDbConnected,
    getSB,
    refreshEmployeesFromDb,
  };
};
